import { TabsActions } from "@mui/material";
import { TabProps } from "@mui/material/Tab";
import { styled } from "@mui/material/styles";
import DojoView from "csi/page/DojoView";
import { getReactComponentInfo } from "csi/service/layoutService";
import session from "csi/session";
import { useStore } from "csi/state/hooks";
import { LayoutItem } from "csi/state/layout";
import { LOCATION_TAB } from "csi/utility/routerUtility";
import { FlexColumnProps } from "designSystem/components/flex/FlexColumn";
import { TabScrollButton } from "designSystem/components/tabs/TabScrollButton";
import { useResizeObserver } from "designSystem/hooks";
import { Box, FlexColumn, Tab, Tabs } from "designSystem/index";
import { debounce } from "lodash";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, KeyboardEvent, useCallback, useLayoutEffect, useRef, useState } from "react";
import LayoutContent from "./LayoutContent";
import ViewXml from "./ViewXml";

const PREFIX = "LayoutTabs";

const classes = {
	flexContent: `${PREFIX}flexContent`,
	tabstrip: `${PREFIX}tabstrip`,
};

const StyledFlexColumn = styled(FlexColumn)(({
	theme: { spacing },
}) => ({
	[`& .${classes.flexContent}`]: {
		overflow: "hidden",

		"& > *": {
			height: "100%",
		},
	},

	[`& .${classes.tabstrip}`]: {
		marginTop: spacing(3),
		paddingLeft: spacing(4),
		paddingRight: spacing(4),
	},
}));

interface LayoutTabProps extends TabProps {
	/** Item associated with the tab */
	item: LayoutItem;

	/** Id associated with the item - has to be passed in because mui reads the prop explicitly */
	value: string;
}

interface LayoutTabStripProps {
	parentItem: LayoutItem;
	useFlexHeight: boolean;
}

const debouncedResolve = debounce((layoutStore, item) => {
	layoutStore.resolveItems([item]);
}, 250);

// List of paths which we already tried to resolve, do not try to resolve more then once
const unresolvedPaths = new Set();

const LayoutTab: FunctionComponent<LayoutTabProps> = observer((props) => {
	const { layoutStore } = useStore();
	const { item, ...otherProps } = props;
	const { cache, id, name, node, urlPath } = item;

	const contentRef = useCallback((node: HTMLElement) => {
		// set name as data attribute (data-csi-tab-name) to support CSS preventing jitter as tabs are selected
		if (node) {
			node.dataset.csiTabName = name;
		}
	}, [name]);

	if (!node) {
		// if node not found for tab, make sure it is not because the client does not yet have it cached;
		// use case is Style Gantt tab appearing after WBS is selected
		const paths = cache.getUnresolvedUrlPaths([urlPath]);
		if (paths.length) {
			const path = urlPath.join("-");
			// Prevent endless circle is user has no rights
			if (!unresolvedPaths.has(path)) {
				debouncedResolve(layoutStore, item);
				unresolvedPaths.add(path);
			}
		}
	}

	return (
		<Tab 
			label={name}
			ref={contentRef}
			value={id}
			{...otherProps}
		/>
	);
});

const LayoutTabStrip: FunctionComponent<LayoutTabStripProps> = observer((props) => {
	const [showXml, setShowXml] = useState(false);
	const { layoutStore, routerStore, sessionStore } = useStore();
	const { viewParams } = layoutStore;
	const { refreshTimestamp } = routerStore;
	const { parentItem, useFlexHeight } = props;
	const { headerViewId, id, node, selectedChild, viewOwnerType, visibleChildren } = parentItem;
	const tabsValue = selectedChild?.id;

	const onChangeTab = async (event: object, value: any) => {
		session.setLocationKey(LOCATION_TAB, value);
	};

	const onKeyUp = action((event: KeyboardEvent<HTMLDivElement>) => {
		if (sessionStore.debugMode && (event.key === "F2")) {
			sessionStore.lastActionElement = null;
			sessionStore.lastActionView = null;
			setShowXml(!showXml);
			event.stopPropagation();
		}
	});

	// workaround for Firefox bug when indicator will not update on tab change
	// known issue. Examples: 
	//     https://stackoverflow.com/questions/61184102/react-material-tabs-indicator-not-showing-on-first-load
	//     https://github.com/mui/material-ui/issues/20527
	//
	// using layout effect as suggested at: https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
	const tabsActions = useRef<TabsActions>();

	useLayoutEffect(() => {
		tabsActions?.current?.updateIndicator();
	});

	// render react component or dojo view based on the type
	const { ReactComponent, reactViewProps, reactView, isReact } = getReactComponentInfo(node, headerViewId);

	return (
		<Fragment>
			{headerViewId && node && (
				isReact ? (
					<ReactComponent {...reactView.properties} {...reactViewProps} />
				) : (
					<DojoView
						fullHeight={useFlexHeight && !tabsValue}
						nodeUrl={node.$URL}
						refreshTimestamp={refreshTimestamp}
						style={tabsValue ? { paddingBottom: 16 } : { height: "100%" }}
						viewId={headerViewId}
						viewParams={viewParams}
					/>
				)
			)}
			{showXml && (
				<ViewXml
					nodeType={viewOwnerType}
					onClose={() => setShowXml(false)}
					viewId={id}
				/>
			)}
			{tabsValue && (
				<Tabs
					ScrollButtonComponent={TabScrollButton}
					action={tabsActions}
					component="div"
					onChange={onChangeTab}
					onKeyUp={onKeyUp}
					value={tabsValue}
					variant="scrollable"
				>
					{
						visibleChildren.map((item) => (
							<LayoutTab
								item={item}
								key={item.uniqueId}
								value={item.id}
							/>
						),
						)
					}
				</Tabs>
			)}
		</Fragment>
	);
});

export interface LayoutTabsProps extends FlexColumnProps { }

export const LayoutTabs: FunctionComponent<LayoutTabsProps> = observer((props) => {
	const { browserStore, layoutStore, layoutVisible, routerStore } = useStore();
	const { fullScreen } = browserStore;
	const { hasTabs, selectedItemsWithTabs, useFlexHeight } = layoutStore;
	const { hideTabs } = routerStore;

	// do not render tab strips if not visible, otherwise generates a console error within MUI
	const renderTabStrips = (!fullScreen || !hasTabs) && !hideTabs && layoutVisible;

	const { ref } = useResizeObserver<HTMLDivElement>({
		onResize: () => {
			// the MUI Tabs component does not use ResizeObserver - it only reacts to resize events;
			// so we need to trigger that behavior ourselves
			window.dispatchEvent(new CustomEvent("resize"));
		},
	});

	return (
		<StyledFlexColumn
			overflow={useFlexHeight ? "hidden" : "auto"}
			{...{ ref }}
			{...props}
		>
			{renderTabStrips &&
				selectedItemsWithTabs.map((item) => (
					<Box
						className={classes.tabstrip}
						flexGrow={hasTabs ? null : 1}
						key={item.uniqueId}
						sx={{
							overflowY: item.scrollableVertically ? "auto" : "",
						}}
					>
						<LayoutTabStrip
							parentItem={item}
							useFlexHeight={useFlexHeight}
						/>
					</Box>
				),
				)
			}
			{hasTabs && (
				<Box
					className={(useFlexHeight || fullScreen) ? classes.flexContent : null}
					flexGrow="1"
				>
					<LayoutContent />
				</Box>
			)}
		</StyledFlexColumn>
	);
});

export default LayoutTabs;